import { Component, OnInit, ViewChild, Injector } from '@angular/core';
import { Router } from '@angular/router';
const screenfull = require('screenfull');

import { UserblockService } from '../sidebar/userblock/userblock.service';
import { SettingsService } from '../../core/settings/settings.service';
import { ThemeService, ThemePreference } from '../../core/theme/theme.service';
import { AlertsService } from '../../chatstyle/alerts/alerts.service';
import { OrganizationService } from '../../chatstyle/organization/organization.service';
import { Organization } from '../../shared/models/core.model';
import { TierService } from '../../shared/services/tier.service';
import { BotService } from '../../chatstyle/bot/bot.service';

import { AuthService } from '../../auth/auth/auth.service';
import { TenantImpersonationService } from '../../auth/tenant-impersonation/tenant-impersonation.service';

import { UserAlert } from '../../shared/models/core.model';
import { AboutDialogComponent } from '../../shared/components/about-dialog/about-dialog.component';
import { OrgNotificationService } from '../../shared/services/org-notification.service';
import { SUPPORT_URL } from '../../shared/support-links';

/**
 * Shown in the gold badge when the impersonated account's name cannot be read.
 * The badge is a safety marker first and a label second — it must never render as
 * a bare icon, which is exactly the state CHAT-959 fixed.
 */
export const IMPERSONATION_FALLBACK_LABEL = 'Impersonating';

/** One row of the Appearance control in the user menu. */
export interface AppearanceOption {
  value: ThemePreference;
  label: string;
  icon: string;
}

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit {

    navCollapsed = true; // for horizontal layout
    menuItems = []; // for horizontal layout
    router: Router;

  alerts: UserAlert[];

  public orgs: Organization[];
  public selectedOrg: Organization;
  private authoritativeTier: string | null = null;
  /**
   * Whether each of the two tier sources has settled, either way.
   *
   * They are tracked separately because they are two independent requests and
   * CHAT-911 was them being chained: /organization/me was issued only from the
   * success path of the org LIST, so a transient list failure left the
   * authoritative tier unread and the badge fell back to the normalised
   * "Starter" for a partner who is not on Starter. See showTierBadge().
   */
  private authoritativeTierSettled = false;
  private orgListSettled = false;

  // Impersonation context. The tenant LIST is no longer loaded here — that was
  // only ever feeding the <select>, which now lives on /partners. All the header
  // needs is the name of the tenant you are currently inside, and only while you
  // are actually impersonating one.
  public isImpersonating = false;
  public currentTenantName: string = '';

  // Tenant badge, quiet state (CHAT-1152). The bot the session is in, named in
  // the bar instead of being prefixed onto every page title. Only shown to a
  // user who could be somewhere else -- see showTenantBadge().
  public currentBotName: string = '';
  private reachableBotCount = 0;

  // Identity. Email rather than a display name: admin invites are email-only, so
  // for most users there is no name to render.
  public userEmail: string = '';
  public userInitial: string = '';

  public themePreference: ThemePreference = 'light';
  public readonly appearanceOptions: AppearanceOption[] = [
    { value: 'system', label: 'System', icon: 'icon-screen-desktop' },
    { value: 'light',  label: 'Light',  icon: 'far fa-sun' },
    { value: 'dark',   label: 'Dark',   icon: 'far fa-moon' },
  ];

    isNavSearchVisible: boolean;
    isRightPanelVisible: boolean = false;
    @ViewChild('fsbutton', { static: true }) fsbutton;  // the fullscreen button
    @ViewChild(AboutDialogComponent, { static: false }) aboutDialog: AboutDialogComponent;

  constructor(
    public userblockService: UserblockService, public settings: SettingsService, public injector: Injector,
    public alertsService: AlertsService,
    public authService: AuthService,
    public orgService: OrganizationService,
    private tierService: TierService,
    private orgNotificationService: OrgNotificationService,
    private themeService: ThemeService,
    private tenantImpersonationService: TenantImpersonationService,
    private botService: BotService) {

    // show only a few items on demo
    this.orgs = [];
    this.selectedOrg = null;
  }

  logout() {
    this.authService.startLogOut();
  }

  ngOnInit() {
        this.isNavSearchVisible = false;
        this.isRightPanelVisible = this.settings.getLayoutSetting('rightPanelOpen') || false;

        var ua = window.navigator.userAgent;
        if (ua.indexOf("MSIE ") > 0 || !!ua.match(/Trident.*rv\:11\./)) { // Not supported under IE
            this.fsbutton.nativeElement.style.display = 'none';
        }

        // Switch fullscreen icon indicator
        //const el = this.fsbutton.nativeElement.firstElementChild;
        //screenfull.on('change', () => {
        //    if (el)
        //        el.className = screenfull.isFullscreen ? 'fa fa-compress' : 'fa fa-expand';
        //});

        this.router = this.injector.get(Router);

        // Autoclose navbar on mobile when route change
        this.router.events.subscribe((val) => {
            // scroll view to top
            window.scrollTo(0, 0);
            // close collapse menu
            this.navCollapsed = true;
        });

    this.refreshAlerts();

    this.resolveIdentity();
    this.themePreference = this.themeService.preference;

    this.orgNotificationService.notificationReceived.subscribe(() => {
      this.refreshAlerts();
    });

    // The authoritative tier, issued on its own. NOT from inside the org-list
    // callback: the list is the FALLBACK source, and chaining the two meant a
    // transient list failure suppressed the authoritative read as well
    // (CHAT-911). OrganizationService dedupes and replays /organization/me, so
    // this costs no extra request even though resolveImpersonatedTenantName()
    // may ask for it too.
    this.resolveAuthoritativeTier();

    // The org list no longer renders anything by itself (v5 dropped the standing
    // org name and the multi-org <select>), but it is still what the notification
    // hub keys off and the fallback source for the tier badge.
    this.orgService.getOrganizations()
      .subscribe(
        result => {

          this.orgs = result;
          if (this.orgs) {
            if (this.orgs.length === 1) {
              this.selectedOrg = this.orgs[0];
            } else {
              for (let i = 0; i < this.orgs.length; i++) {
                if (this.orgs[i].userActive === true) {
                  this.selectedOrg = this.orgs[i];
                }
              }
              if (!this.selectedOrg) {
                this.selectedOrg = this.orgs[0];
              }
           }
           }

          this.orgListSettled = true;

          if (this.selectedOrg?.id) {
            this.orgNotificationService.connect(this.selectedOrg.id);
          }
        },
        error => {
          this.orgListSettled = true;
          console.error(error);
        }
      );

    this.isImpersonating = this.tenantImpersonationService.isImpersonating();

    if (this.isImpersonating) {
      this.resolveImpersonatedTenantName();
    }

    this.resolveTenantBadge();
  }

  /**
   * The quiet state of the tenant badge: which bot this session is in.
   *
   * Page titles used to carry the bot name ("Acme Bot - Commands") so a
   * SuperAdmin moving between tenants knew where they were. That put a
   * single-bot partner's own bot name at the top of every page they ever opened,
   * and still said nothing to a SuperAdmin who was not impersonating. The name
   * lives here now, once, and only for someone it can tell anything.
   *
   * Both requests are already made by the pages under this shell and BotService
   * caches them, so this adds no load of its own. A failure leaves the badge
   * hidden -- unlike the impersonation badge this is orientation, not a safety
   * marker, so absence is the right degradation.
   */
  private resolveTenantBadge() {
    this.botService.getBot().subscribe({
      next: (bot) => { this.currentBotName = bot?.name?.trim() || ''; },
      error: (err) => console.error('Error resolving current bot for the tenant badge:', err)
    });
    this.botService.getBots().subscribe({
      next: (bots) => { this.reachableBotCount = bots?.length ?? 0; },
      error: (err) => console.error('Error counting bots for the tenant badge:', err)
    });
  }

  /**
   * Show the quiet badge only when the signed-in user can reach more than one
   * bot. Derived from reach rather than role, so a partner with several bots or
   * orgs gets it too without a special case; a SuperAdmin can reach every
   * tenant by impersonation, so for them the answer is always yes.
   *
   * A getter, not a flag set in ngOnInit: the profile carrying the SuperAdmin
   * privilege can land after the header initialises, and the badge must follow
   * it. While impersonating, the loud badge already names the tenant.
   */
  showTenantBadge(): boolean {
    if (this.isImpersonating || !this.currentBotName) {
      return false;
    }
    return this.reachableBotCount > 1
      || (this.orgs?.length ?? 0) > 1
      || this.tenantImpersonationService.isSuperAdmin();
  }

  /**
   * Put a partner NAME in the gold badge.
   *
   * This previously keyed off `claims.tenant` and looked the database name up in the
   * SuperAdmin tenant list, and it rendered a bare eye with no name at all (CHAT-959).
   * The portal runs with `autoUserInfo: false`, so `userData` is the id_token payload —
   * and the auth server gives `tenant` an access-token-only destination, while
   * `impersonator_id` and `original_tenant` get both. That is why the badge appeared
   * and Stop worked while the name alone was missing: the lookup key was always '',
   * so nothing in the list ever matched and the fallback was '' too.
   *
   * `organization/me` needs no new claim and no SuperAdmin-only endpoint. The API
   * routes it by the access token's tenant, so while impersonating it answers with the
   * impersonated account — the same Organization name the Partners list labels that
   * tenant with (CHAT-555). The header already requests it for the tier badge and
   * OrganizationService replays the in-flight response, so this adds no HTTP call.
   *
   * That routing is worth spelling out, because the failure this would cause is the
   * badge naming the WRONG tenant rather than falling back — a safety marker lying is
   * worse than a safety marker being vague. The chain: TenantResolutionMiddleware sets
   * TenantContext from the access token's `tenant` claim (and logs "Impersonated
   * request: SuperAdmin X acting on tenant Y" off that same claim); during
   * impersonation ChatAuthorizationFlow.ResolveTenantAsync sets that claim to the
   * TARGET tenant; and GetMyOrganizationAsync is a bare
   * `_db.Organizations.FirstOrDefaultAsync()` on the tenant-scoped context — one org
   * per tenant, no user or org id read from the token. So there is no path by which
   * this returns the impersonator's own account.
   */
  private resolveImpersonatedTenantName() {
    // Never blank: this is a safety marker, so it degrades rather than vanishes —
    // both before the response lands and if it never does.
    this.currentTenantName = IMPERSONATION_FALLBACK_LABEL;

    this.orgService.getMyOrganization().subscribe({
      next: (org) => {
        this.currentTenantName = org?.name?.trim() || IMPERSONATION_FALLBACK_LABEL;
      },
      error: (err) => {
        this.currentTenantName = IMPERSONATION_FALLBACK_LABEL;
        console.error('Error resolving impersonated tenant name:', err);
      }
    });
  }

  /**
   * Read the tier from /organization/me, the Organization table itself, rather
   * than the per-user list's copy of it.
   *
   * Independent of getOrganizations() on purpose — see the field comment on
   * authoritativeTierSettled. The two requests answer separately and either may
   * fail without taking the other's answer with it.
   */
  private resolveAuthoritativeTier() {
    this.orgService.getMyOrganization().subscribe({
      next: (org) => {
        this.authoritativeTier = org?.tier || null;
        this.authoritativeTierSettled = true;
      },
      error: (err) => {
        // Leave the badge to the list value (or to the normalised default)
        // rather than hiding it for the rest of the session.
        this.authoritativeTierSettled = true;
        console.error('Error loading authoritative tier:', err);
      }
    });
  }

  private resolveIdentity() {
    const claims = this.authService.getClaims() || {};
    this.userEmail = claims.email || claims.preferred_username || claims.name || '';
    this.userInitial = this.userEmail ? this.userEmail.charAt(0).toUpperCase() : '';
  }

  refreshAlerts() {
    this.alertsService.getActive().subscribe({
      next: (result) => { this.alerts = result; },
      error: (err) => console.error(err)
    });
  }

  clearAlerts() {
    this.alertsService.resolveAll().subscribe({
      next: () => this.refreshAlerts(),
      error: (err) => console.error(err)
    });
  }

  stopImpersonation() {
    this.tenantImpersonationService.stopImpersonation();
  }

  /**
   * The Support destination in the user menu. Shared with the About dialog so
   * the product has one Support page, not two — see shared/support-links.ts.
   */
  readonly supportUrl = SUPPORT_URL;

  /**
   * The Knowledge Base target for the Help menu.
   *
   * A getter, not a field resolved in ngOnInit: SettingsService fills itself from
   * GET /settings asynchronously, so reading `help_uri` once at init races the
   * response and would silently drop the KB row on a fast paint.
   */
  get knowledgeBaseUrl(): string {
    return this.settings.getSetting('help_uri') || '';
  }

  /**
   * Appearance is available to every admin (CHAT-925 phase 2). The SuperAdmin
   * gate that stood here existed for one reason -- until the shared substrates
   * were tokenized, opting in bought you a dark shell around a light body -- and
   * CHAT-915 closed that in #323/#325, so the gate no longer protects anything.
   *
   * This exposes the CHOICE, not a new default: ThemeService.DEFAULT_PREFERENCE
   * is still `light`, so an admin who never opens this menu sees exactly what
   * they saw before. Flipping the default to `system` is CHAT-925 phase 3.
   *
   * The one remaining reason to hide it is a route pinned to light: the control
   * cannot take effect there, and a toggle that visibly does nothing reads as a
   * bug. Checked live rather than cached -- the pin changes on navigation.
   */
  showAppearance(): boolean {
    return !this.themeService.isForcedLight;
  }

  setThemePreference(preference: ThemePreference) {
    if (!this.showAppearance()) {
      return;
    }
    this.themeService.setPreference(preference);
    this.themePreference = this.themeService.preference;
  }

  activeAlerts() {
      let count = 0;

      if (this.alerts && this.alerts.length > 0) {
        for (let i = 0; i < this.alerts.length; i++) {
          if (!this.alerts[i].resolved) {
            count++;
          }
        }
      }

      return count;
    }

    toggleUserBlock(event) {
        event.preventDefault();
        this.userblockService.toggleVisibility();
    }

    openNavSearch(event) {
        event.preventDefault();
        event.stopPropagation();
        this.setNavSearchVisible(true);
    }

    setNavSearchVisible(stat: boolean) {
        // console.log(stat);
        this.isNavSearchVisible = stat;
    }

    getNavSearchVisible() {
        return this.isNavSearchVisible;
    }

    toggleCollapsedSideabar() {
        this.settings.toggleLayoutSetting('isCollapsed');
    }

    isCollapsedText() {
        return this.settings.getLayoutSetting('isCollapsedText');
    }

    toggleFullScreen(event) {
        if (screenfull.enabled) {
            screenfull.toggle();
        }
  }

  openAbout() {
    this.aboutDialog.open();
  }

  private getActiveTier(): string | null {
    return this.authoritativeTier ?? this.selectedOrg?.tier ?? null;
  }

  /**
   * Hold the badge back until the tier is actually known.
   *
   * TierService normalises an unknown tier to a displayable "Starter", so
   * rendering unconditionally flashes "Starter" on every page load while
   * /organization/me is still in flight — which reads as a plan downgrade now
   * that the badge sits next to the product name rather than off to the right.
   *
   * Two ways to be ready, and the first is the common one:
   *
   *   · the authoritative read answered with a tier — nothing the list can add,
   *     so show it without waiting for the list at all;
   *   · otherwise BOTH sources have settled, so the fallback has had its turn
   *     and whatever we have is final. An org whose tier really is null still
   *     shows the normalised Starter badge, exactly as it did before.
   *
   * The second clause is what CHAT-911 changed: the gate used to be satisfied by
   * the authoritative request alone, which the list's error path could prevent
   * from ever being issued.
   */
  showTierBadge(): boolean {
    return this.authoritativeTier !== null
      || (this.authoritativeTierSettled && this.orgListSettled);
  }

  getTierDisplayName(): string {
    return this.tierService.getTierDisplayName(this.getActiveTier());
  }

  getTierIcon(): string {
    return this.tierService.getTierIcon(this.getActiveTier());
  }

  getTierBadgeClass(): string {
    return this.tierService.getTierBadgeClass(this.getActiveTier());
  }

  toggleRightPanel(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    this.isRightPanelVisible = !this.isRightPanelVisible;
    this.settings.setLayoutSetting('rightPanelOpen', this.isRightPanelVisible);
  }

  getRightPanelVisible(): boolean {
    return this.isRightPanelVisible;
  }

  openBeamer(): void {
    const beamer = (window as any).Beamer;
    if (!beamer) return;
    if (!beamer.initialized) {
      beamer.init();
    }
    beamer.show();
  }

  private setBeamerIdentity(claims: any, attempt = 0): void {
    const maxAttempts = 5;
    if ((window as any).Beamer?.update) {
      (window as any).Beamer.update({
        user_id: claims.sub || '',
        user_email: claims.email || '',
        user_firstname: claims.given_name || claims.name || ''
      });
    } else if (attempt < maxAttempts) {
      setTimeout(() => this.setBeamerIdentity(claims, attempt + 1), 100 * Math.pow(2, attempt));
    } else {
      console.warn('Beamer SDK not ready; user identity not set.');
    }
  }

  isRightPanelAvailable(): boolean {
    const rightPanelUrl = this.settings.getSetting('rightPanelUrl');
    return rightPanelUrl && rightPanelUrl !== 'about:blank' && rightPanelUrl.trim() !== '';
  }

}
